import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Cookie, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { initMetaPixel, trackPageView } from '../lib/metaPixel';

const COOKIE_CONSENT_KEY = 'smartballoon-cookie-consent';

/**
 * CookieBanner with GDPR compliant cookie categories:
 * - Essential: Always enabled (authentication, app functionality)
 * - Analytics: Optional (usage analytics)
 * - Marketing: Optional (Meta Pixel, advertising)
 */
const CookieBanner = () => {
  const { i18n } = useTranslation();
  const [showBanner, setShowBanner] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true
    analytics: false,
    marketing: false,
  });
  const lang = i18n.language?.split('-')[0] || 'it';

  // Try to read consent from any available storage
  const getConsent = () => {
    try {
      const val = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (val) return JSON.parse(val);
    } catch (e) { /* blocked */ }
    
    try {
      const val = sessionStorage.getItem(COOKIE_CONSENT_KEY);
      if (val) return JSON.parse(val);
    } catch (e) { /* blocked */ }
    
    if (typeof window !== 'undefined' && window.__cookieConsent) {
      return typeof window.__cookieConsent === 'string' 
        ? JSON.parse(window.__cookieConsent) 
        : window.__cookieConsent;
    }
    
    return null;
  };

  // Save consent to all available storage methods
  const setConsent = (value) => {
    const consentData = {
      ...value,
      timestamp: new Date().toISOString(),
      version: '2.0' // Version with marketing cookies
    };
    const jsonValue = JSON.stringify(consentData);
    
    // Set window flags FIRST (most reliable)
    if (typeof window !== 'undefined') {
      window.__cookieConsent = consentData;
      
      // Set Meta Pixel consent flag
      if (value.marketing) {
        window.__metaPixelConsent = true;
        console.log('[CookieBanner] Marketing consent granted, setting __metaPixelConsent = true');
      } else {
        window.__metaPixelConsent = false;
      }
    }
    
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, jsonValue);
      console.log('[CookieBanner] Saved to localStorage:', consentData);
    } catch (e) { 
      console.log('[CookieBanner] localStorage blocked');
    }
    
    try {
      sessionStorage.setItem(COOKIE_CONSENT_KEY, jsonValue);
    } catch (e) { 
      console.log('[CookieBanner] sessionStorage blocked');
    }
    
    // Initialize Meta Pixel if marketing consent given
    if (value.marketing) {
      // Small delay to ensure flags are set
      setTimeout(() => {
        initMetaPixel();
        trackPageView(window.location.pathname);
      }, 100);
    }
    
    // Dispatch event for other components to react
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: consentData }));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const consent = getConsent();
      console.log('[CookieBanner] Initial consent check:', consent);
      
      if (consent && consent.timestamp) {
        setShowBanner(false);
        
        // Set window flags from stored consent
        if (typeof window !== 'undefined') {
          window.__cookieConsent = consent;
          if (consent.marketing) {
            window.__metaPixelConsent = true;
            console.log('[CookieBanner] Restored marketing consent, initializing pixel');
            initMetaPixel();
          }
        }
      }
      setMounted(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  const handleAcceptAll = () => {
    setConsent({
      essential: true,
      analytics: true,
      marketing: true,
    });
    setShowBanner(false);
  };

  const handleAcceptSelected = () => {
    setConsent({
      essential: true,
      analytics: preferences.analytics,
      marketing: preferences.marketing,
    });
    setShowBanner(false);
  };

  const handleRejectNonEssential = () => {
    setConsent({
      essential: true,
      analytics: false,
      marketing: false,
    });
    setShowBanner(false);
  };

  const content = {
    it: {
      title: "Utilizziamo i Cookie",
      description: "SmartBalloon utilizza cookie per migliorare la tua esperienza e per analisi di marketing.",
      acceptAll: "Accetta tutti",
      acceptSelected: "Salva preferenze",
      rejectNonEssential: "Solo essenziali",
      moreInfo: "Maggiori informazioni",
      customize: "Personalizza",
      hideDetails: "Nascondi dettagli",
      categories: {
        essential: {
          name: "Cookie Essenziali",
          description: "Necessari per il funzionamento dell'app (autenticazione, sessione). Non possono essere disattivati.",
        },
        analytics: {
          name: "Cookie Analitici",
          description: "Ci aiutano a capire come utilizzi l'app per migliorarla.",
        },
        marketing: {
          name: "Cookie di Marketing",
          description: "Utilizzati per mostrarti annunci pertinenti e misurare l'efficacia delle campagne pubblicitarie (Meta Pixel).",
        },
      },
    },
    en: {
      title: "We Use Cookies",
      description: "SmartBalloon uses cookies to improve your experience and for marketing analytics.",
      acceptAll: "Accept all",
      acceptSelected: "Save preferences",
      rejectNonEssential: "Essential only",
      moreInfo: "More information",
      customize: "Customize",
      hideDetails: "Hide details",
      categories: {
        essential: {
          name: "Essential Cookies",
          description: "Required for app functionality (authentication, session). Cannot be disabled.",
        },
        analytics: {
          name: "Analytics Cookies",
          description: "Help us understand how you use the app to improve it.",
        },
        marketing: {
          name: "Marketing Cookies",
          description: "Used to show you relevant ads and measure advertising campaign effectiveness (Meta Pixel).",
        },
      },
    },
  };

  const c = content[lang] || content.en;

  if (!mounted) return null;
  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[9999] p-4 animate-in slide-in-from-bottom duration-300"
      style={{ pointerEvents: 'auto' }}
      data-testid="cookie-banner"
    >
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 p-6">
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex w-12 h-12 bg-amber-100 rounded-xl items-center justify-center flex-shrink-0">
            <Cookie className="w-6 h-6 text-amber-600" />
          </div>
          
          <div className="flex-1">
            <h3 className="font-semibold text-slate-800 text-lg mb-1">{c.title}</h3>
            <p className="text-slate-600 text-sm mb-4">{c.description}</p>
            
            {/* Cookie Categories (expandable) */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mb-3"
            >
              {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {showDetails ? c.hideDetails : c.customize}
            </button>
            
            {showDetails && (
              <div className="space-y-3 mb-4 p-4 bg-slate-50 rounded-lg">
                {/* Essential - Always on */}
                <div className="flex items-start gap-3">
                  <input 
                    type="checkbox" 
                    checked={true} 
                    disabled 
                    className="mt-1 w-4 h-4 rounded"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-sm text-slate-800">{c.categories.essential.name}</p>
                    <p className="text-xs text-slate-500">{c.categories.essential.description}</p>
                  </div>
                </div>
                
                {/* Analytics */}
                <div className="flex items-start gap-3">
                  <input 
                    type="checkbox" 
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences(p => ({ ...p, analytics: e.target.checked }))}
                    className="mt-1 w-4 h-4 rounded cursor-pointer"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-sm text-slate-800">{c.categories.analytics.name}</p>
                    <p className="text-xs text-slate-500">{c.categories.analytics.description}</p>
                  </div>
                </div>
                
                {/* Marketing */}
                <div className="flex items-start gap-3">
                  <input 
                    type="checkbox" 
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences(p => ({ ...p, marketing: e.target.checked }))}
                    className="mt-1 w-4 h-4 rounded cursor-pointer"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-sm text-slate-800">{c.categories.marketing.name}</p>
                    <p className="text-xs text-slate-500">{c.categories.marketing.description}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Buttons */}
            <div className="flex flex-wrap items-center gap-3">
              <Button 
                onClick={handleAcceptAll}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="cookie-accept-all"
              >
                {c.acceptAll}
              </Button>
              {showDetails && (
                <Button 
                  variant="outline"
                  onClick={handleAcceptSelected}
                  data-testid="cookie-accept-selected"
                >
                  {c.acceptSelected}
                </Button>
              )}
              <Button 
                variant="outline"
                onClick={handleRejectNonEssential}
                data-testid="cookie-reject-non-essential"
              >
                {c.rejectNonEssential}
              </Button>
              <Link 
                to="/cookies" 
                className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
              >
                {c.moreInfo}
              </Link>
            </div>
          </div>
          
          <button 
            onClick={handleRejectNonEssential}
            className="text-slate-400 hover:text-slate-600 p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
